import { useCallback, useMemo, useReducer } from 'react';
import {
    calculatePageContainingIndex,
    determinePagesToLoad,
    toItemArray,
    updateWithPageError,
    updateWithPageLoad,
    updateWithPageRequests
} from './pagination';
import { normalizeItem } from './items';
import { fetchApiPage, fetchItemIndex } from './api';
import { sleep } from '../../../utils/timeout';

export const PAGE_SIZE = 40;

export const REQUEST_LIST_PAGES = 'REQUEST_LIST_PAGES';
export const LIST_PAGE_ERROR = 'LIST_PAGE_ERROR';
export const LIST_PAGE_LOADED = 'LIST_PAGE_LOADED';
export const REQUEST_SEARCH_PAGES = 'REQUEST_SEARCH_PAGES';
export const SEARCH_PAGE_ERROR = 'SEARCH_PAGE_ERROR';
export const SEARCH_PAGE_LOADED = 'SEARCH_PAGE_LOADED';
export const UPDATE_SEARCH_QUERY = 'UPDATE_SEARCH_QUERY';

/** @type {PlanPickerApiState} */
const initialState = {
    itemsById: {},
    list: {
        pages: {},
        total: 'unknown'
    },
    search: {
        pages: {},
        query: '',
        total: 'unknown'
    }
};

/** @type {import("react").Reducer<PlanPickerApiState['itemsById'], PlanPickerApiAction} */
function itemsByIdReducer(prevState, action) {
    switch (action.type) {
        case LIST_PAGE_LOADED:
        case SEARCH_PAGE_LOADED: {
            const newState = { ...prevState };

            const { items } = action.payload;
            const normalizedItems = items.map(item => normalizeItem(item));

            for (const item of normalizedItems) {
                newState[item.id] = item;
            }

            return newState;
        }
        default:
            return prevState;
    }
}

/** @type {import("react").Reducer<PlanPickerApiState['list'], PlanPickerApiAction} */
function listReducer(prevState, action) {
    switch (action.type) {
        case REQUEST_LIST_PAGES:
            return updateWithPageRequests(prevState, action.payload.pageNumbers);
        case LIST_PAGE_ERROR:
            return updateWithPageError(prevState, action.payload.pageNumber);
        case LIST_PAGE_LOADED:
            return updateWithPageLoad(prevState, action.payload);
        default:
            return prevState;
    }
}

/** @type {import("react").Reducer<PlanPickerApiState['search'], PlanPickerApiAction} */
function searchReducer(prevState, action) {
    switch (action.type) {
        case REQUEST_SEARCH_PAGES:
            return updateWithPageRequests(prevState, action.payload.pageNumbers);
        case SEARCH_PAGE_ERROR:
            return updateWithPageError(prevState, action.payload.pageNumber);
        case SEARCH_PAGE_LOADED:
            return updateWithPageLoad(prevState, action.payload);
        case UPDATE_SEARCH_QUERY:
            if (action.payload.query === prevState.query) {
                return prevState;
            } else {
                // Invalidate all current pages when the search query changes
                return {
                    ...initialState.search,
                    query: action.payload.query
                };
            }
        default:
            return prevState;
    }
}

/**
 * @param {PlanPickerApiAction} action
 * @returns {boolean}
 */
const canBeStale = action => {
    switch (action.type) {
        case REQUEST_SEARCH_PAGES:
        case SEARCH_PAGE_ERROR:
        case SEARCH_PAGE_LOADED:
            return true;
        default:
            return false;
    }
};

/**
 * @param {PlanPickerApiState} prevState
 * @param {PlanPickerApiAction} action
 */
function isStaleUpdate(prevState, action) {
    if (!canBeStale(action)) {
        return false;
    }

    const doesSearchQueryMatch = action.payload.query === prevState.search.query;
    return !doesSearchQueryMatch;
}

/** @type {import("react").Reducer<PlanPickerApiState, PlanPickerApiAction>} */
export function reducer(prevState, action) {
    // Ignore stale actions that no longer apply to the current state (ex. page loads
    // for a previous search query).
    if (isStaleUpdate(prevState, action)) {
        return prevState;
    }

    return {
        itemsById: itemsByIdReducer(prevState.itemsById, action),
        list: listReducer(prevState.list, action),
        search: searchReducer(prevState.search, action)
    };
}

const SEARCH_MINIMUM_LOADING_MS = 300;

/**
 * @param {PlanPickerApiProps} props
 * @returns {[state: PlanPickerApiState, helpers: PlanPickerApiHelpers]}
 */
export function usePlanPickerApi({ url, token }) {
    const [state, dispatch] = useReducer(reducer, initialState);

    // Selecting some nested state for use in helper callbacks
    const { list: listState, search: searchState, itemsById } = state;
    const { query: searchQuery } = searchState;

    /** @type {(pageNumber: import('./pagination').PageNumber, signal: AbortSignal) => Promise<void>} */
    const loadListPage = useCallback(
        (pageNumber, signal) => {
            fetchApiPage(url, { token, pageNumber, pageSize: PAGE_SIZE }, signal)
                .then(pageData => {
                    if (!signal.aborted) {
                        dispatch({
                            type: LIST_PAGE_LOADED,
                            payload: {
                                pageNumber,
                                items: pageData.data,
                                total: pageData.total
                            }
                        });
                    }
                })
                .catch(() => {
                    if (!signal.aborted) {
                        dispatch({
                            type: LIST_PAGE_ERROR,
                            payload: { pageNumber }
                        });
                    }
                });
        },
        [token, url]
    );

    /**
     * Loads the page containing the selected plan when necessary,
     * so that the selected plan is visible in the options menu list
     * e.g. when a plan is selected from search results
     */
    /** @type {(id: string) => void} */
    const loadPageForSelectedItem = useCallback(
        id => {
            const getPageNumberWithItemId = id => {
                return Object.keys(listState.pages)
                    .find(pageNumber => {
                        const { items } = listState.pages[pageNumber];
                        return items ? items.find(itemId => itemId === id) : false;
                    });
            };

            const pageNumber = getPageNumberWithItemId(id);
            if (pageNumber !== undefined) return;

            fetchItemIndex(url, { id, token })
                .then((data) => {
                    const { ix } = data;
                    const controller = new AbortController();
                    loadListItemsByIndex([ix], controller.signal);
                })
                .catch(error => {
                    console.error(error);
                });
        },
        [url, token, listState]
    );

    /** @type {(pageNumber: import('./pagination').PageNumber, signal: AbortSignal, config?: { minimumLoadingMs?: number }) => Promise<void>} */
    const loadSearchPage = useCallback(
        (pageNumber, signal, { minimumLoadingMs = 0 } = {}) => {
            let pagePromise = fetchApiPage(
                url,
                { token, pageNumber, pageSize: PAGE_SIZE, query: searchQuery },
                signal
            );

            if (minimumLoadingMs > 0) {
                pagePromise = Promise.all([pagePromise, sleep(minimumLoadingMs)]).then(
                    ([pageData]) => pageData
                );
            }

            pagePromise
                .then(pageData => {
                    if (!signal.aborted) {
                        dispatch({
                            type: SEARCH_PAGE_LOADED,
                            payload: {
                                pageNumber,
                                items: pageData.data,
                                total: pageData.total,
                                query: searchQuery
                            }
                        });
                    }
                })
                .catch(() => {
                    if (!signal.aborted) {
                        dispatch({
                            type: SEARCH_PAGE_ERROR,
                            payload: { pageNumber }
                        });
                    }
                });
        },
        [token, url, searchQuery]
    );

    /** @type {(initialIndex: import('./pagination').Index, signal: AbortSignal) => void} */
    const loadInitialListData = useCallback(
        (initialIndex, signal) => {
            const initialPageNumber = calculatePageContainingIndex(initialIndex ?? 0, PAGE_SIZE);
            const initialPage = listState.pages[initialPageNumber];
            if (!initialPage || initialPage.status === 'error') {
                loadListPage(initialPageNumber, signal);
            }
        },
        [listState, loadListPage]
    );

    /** @type {(signal: AbortSignal) => void} */
    const loadInitialSearchData = useCallback(
        signal => {
            const firstPage = searchState.pages[1];
            if (!firstPage || firstPage.status === 'error') {
                // To prevent jarring flashes where the plan picker shows a loading message
                // very briefly (< 100ms) before displaying the first page of loaded results,
                // we introduce a minimum amount of time to spend in the "loading" state
                // before showing results. This is only applicable when loading the initial
                // data because only the jump from loading message to loaded results is
                // visually jarring; lazy loading subsequent pages don't cause any layout
                // shifts or other noticeable jank.
                loadSearchPage(1, signal, { minimumLoadingMs: SEARCH_MINIMUM_LOADING_MS });
            }
        },
        [searchState, loadSearchPage]
    );

    /** @type {(id: string) => import('./items').PlanPickerItem} */
    const getItem = useCallback(id => itemsById[id], [itemsById]);

    const getListItems = useCallback(() => {
        return toItemArray(listState, PAGE_SIZE, getItem);
    }, [listState, getItem]);

    /** @type {(indexes: import('./pagination').Index[], signal: AbortSignal) => void} */
    const loadListItemsByIndex = useCallback(
        (indexes, signal) => {
            const pagesToLoad = determinePagesToLoad(listState, PAGE_SIZE, indexes);
            if (pagesToLoad.length) {
                dispatch({
                    type: REQUEST_LIST_PAGES,
                    payload: { pageNumbers: pagesToLoad }
                });
                for (const pageNumber of pagesToLoad) {
                    loadListPage(pageNumber, signal);
                }
            }
        },
        [listState, loadListPage]
    );

    const getSearchItems = useCallback(() => {
        return toItemArray(searchState, PAGE_SIZE, getItem);
    }, [getItem, searchState]);

    /** @type {(indexes: import('./pagination').Index[], signal: AbortSignal) => void} */
    const loadSearchItemsByIndex = useCallback(
        (indexes, signal) => {
            const pagesToLoad = determinePagesToLoad(searchState, PAGE_SIZE, indexes);
            if (pagesToLoad.length) {
                dispatch({
                    type: REQUEST_SEARCH_PAGES,
                    payload: { pageNumbers: pagesToLoad }
                });
                for (const pageNumber of pagesToLoad) {
                    loadSearchPage(pageNumber, signal);
                }
            }
        },
        [loadSearchPage, searchState]
    );

    /** @type {(query: string) => void} */
    const updateSearchQuery = useCallback(query => {
        dispatch({
            type: UPDATE_SEARCH_QUERY,
            payload: { query }
        });
    }, []);

    const helpers = useMemo(
        () => ({
            loadInitialListData,
            getListItems,
            loadPageForSelectedItem,
            loadListItemsByIndex,
            loadInitialSearchData,
            getSearchItems,
            loadSearchItemsByIndex,
            updateSearchQuery
        }),
        [
            updateSearchQuery,
            getListItems,
            loadPageForSelectedItem,
            getSearchItems,
            loadInitialListData,
            loadInitialSearchData,
            loadListItemsByIndex,
            loadSearchItemsByIndex
        ]
    );

    return useMemo(() => [state, helpers], [state, helpers]);
}

/**
 * @typedef {object} PlanPickerApiProps
 * @prop {string} url
 * @prop {string} token
 */

/**
 * @typedef {object} PlanPickerApiState
 * @prop {Record<string, import('./items').PlanPickerItem>} itemsById
 * @prop {import('./pagination').PaginationState} list
 * @prop {import('./pagination').PaginationState & { query: string }} search
 */

/**
 * @typedef {object} PlanPickerApiHelpers
 * @prop {(initialIndex: number, signal: AbortSignal) => void} loadInitialListData
 * @prop {() => PlanPickerItemWithStatus[]} getListItems
 * @prop {(indexes: import('./pagination').Index[], signal: AbortSignal) => void} loadListItemsByIndex
 * @prop {(signal: AbortSignal) => void} loadInitialSearchData
 * @prop {() => PlanPickerItemWithStatus[]} getSearchItems
 * @prop {(indexes: import('./pagination').Index[], signal: AbortSignal) => void} loadSearchItemsByIndex
 * @prop {(query: string) => void} updateSearchQuery
 */

/** @typedef {object} PlanPickerApiAction */
